import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { toast } from './Toast';


function modalHost(): HTMLElement {
  let el = document.getElementById('modals');
  if (!el) {
    el = document.createElement('div');
    el.id = 'modals';
    document.body.appendChild(el);
  }
  return el;
}

type ModalProps = {
  title: string;
  wide?: boolean;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
};

export function Modal({ title, wide, onClose, footer, children }: ModalProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    const first = rootRef.current?.querySelector<HTMLElement>('input,select,textarea,button');
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return createPortal(
    <div
      className="overlay"
      ref={rootRef}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-btn"
            data-close
            style={{ marginLeft: 'auto' }}
            aria-label="Закрыть"
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer !== undefined && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    modalHost(),
  );
}

type ConfirmProps = {
  title: string;
  children: ReactNode;
  yes?: string;

  word?: string;
  onYes: () => void | Promise<void>;
  onClose: () => void;
};

export function ConfirmDialog({ title, children, yes = 'Удалить', word, onYes, onClose }: ConfirmProps) {
  const [typed, setTyped] = useState('');
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" data-close onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-danger"
            id="cfYes"
            onClick={() => {
              if (word && typed.trim() !== word) {
                toast(`Введите «${word}», чтобы подтвердить`, 'err');
                return;
              }
              onClose();
              void onYes();
            }}
          >
            {yes}
          </button>
        </>
      }
    >
      <div className="note" style={{ borderLeftColor: 'var(--err)' }}>
        {children}
      </div>
      {word && (
        <div className="field-row">
          <label htmlFor="cfW">
            Введите <code>{word}</code> для подтверждения
          </label>
          <input
            type="text"
            id="cfW"
            spellCheck={false}
            autoComplete="off"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}
